import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type { CorpusFeed } from "@shared/types/corpus/api.ts";
import { FieldPicker } from "../../../../components/domain/inputs/FieldPicker";
import { gaps } from "../../../../components/primitives/spacing";
import { useSubjectNames } from "../../../../hooks/useSubjectNames";
import {
  DOCUMENT_STATUS_FILTERS,
  type DocumentListFilters,
  type DocumentStatusFilter,
} from "./documentListFilters";

const STATUS_LABELS: Record<DocumentStatusFilter, string> = {
  all: "All",
  pending: "Pending",
  ready: "Ready",
  ingested: "Ingested",
};

interface SubjectFilterChipProps {
  subject: string;
  onDelete: () => void;
}

/** The main subject the list is narrowed to, by its name once that is known, else its id. */
function SubjectFilterChip({ subject, onDelete }: SubjectFilterChipProps) {
  const names = useSubjectNames([subject]);
  return (
    <Chip size="small" label={`Main subject: ${names[subject] ?? subject}`} onDelete={onDelete} />
  );
}

interface DocumentFiltersProps {
  filters: DocumentListFilters;
  onChange: (filters: DocumentListFilters) => void;
  /** The feeds to choose from. */
  feeds: CorpusFeed[];
}

/** The controls that narrow the Documents list: status, health fields (any of them), the feed
 *  that returned a paper and, when a link names one, a main subject. */
export function DocumentFilters({ filters, onChange, feeds }: DocumentFiltersProps) {
  const selectedFeed = feeds.find((feed) => feed.id === filters.feedId) ?? null;

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={gaps.base}
      sx={{ alignItems: "flex-start" }}
    >
      <TextField
        select
        size="small"
        label="Status"
        value={filters.status}
        onChange={(e) => onChange({ ...filters, status: e.target.value as DocumentStatusFilter })}
        sx={{ minWidth: 150 }}
      >
        {DOCUMENT_STATUS_FILTERS.map((status) => (
          <MenuItem key={status} value={status}>
            {STATUS_LABELS[status]}
          </MenuItem>
        ))}
      </TextField>

      <Stack sx={{ flex: 1, minWidth: 0, width: "100%" }}>
        <FieldPicker
          size="small"
          value={filters.fields}
          onChange={(fields) => onChange({ ...filters, fields })}
        />
      </Stack>

      <Autocomplete
        size="small"
        options={feeds}
        value={selectedFeed}
        onChange={(_, feed) => onChange({ ...filters, feedId: feed?.id ?? null })}
        getOptionLabel={(feed) => feed.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={(params) => <TextField {...params} label="Feed" />}
        sx={{ minWidth: 220, width: { xs: "100%", md: 260 } }}
      />

      {filters.subject ? (
        // Keyed by the subject, so another subject looks its name up afresh.
        <SubjectFilterChip
          key={filters.subject}
          subject={filters.subject}
          onDelete={() => onChange({ ...filters, subject: null })}
        />
      ) : null}
    </Stack>
  );
}
